// packages/web-ui/src/components/kb-change-tree/ReviewChipContextMenu.tsx
//
// Story W4-S4 AC #3 — explicit-options menu opened on long-press, right-click,
// or `Enter` from the parent. UX §10.4 lines 1346–1356.

import React, { useEffect, useRef } from 'react';
import type { KbEntityChange, KbEntityStatus } from '@tessa-lean/base';

export type ReviewChipMenuAction =
  | { kind: 'accept' }
  | { kind: 'reject' }
  | { kind: 'pending' }
  | { kind: 'open-file' }
  | { kind: 'open-in-syside' }
  | { kind: 'show-sysml-preview' }
  | { kind: 'show-svg-preview' }
  | { kind: 'why-proposed' };

export interface ReviewChipContextMenuProps {
  open: boolean;
  anchor: { x: number; y: number };
  entity: KbEntityChange;
  status: KbEntityStatus;
  blocked: boolean;
  /** When false, hide the "Open in syside IDE" menu item per FR-SYSIDE-1. */
  sysideAvailable?: boolean;
  onAction: (action: ReviewChipMenuAction) => void;
  onClose: () => void;
}

interface MenuItemSpec {
  action: ReviewChipMenuAction;
  label: string;
  glyph?: string;
  separator?: boolean;
  disabledReason?: string;
}

export function ReviewChipContextMenu({
  open,
  anchor,
  entity,
  status,
  blocked,
  sysideAvailable = true,
  onAction,
  onClose,
}: ReviewChipContextMenuProps): React.ReactElement | null {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<HTMLButtonElement[]>([]);

  // Disabled-state derivation
  const acceptDisabled = blocked || status === 'accepted' || status === 'applied';
  const rejectDisabled = status === 'rejected';
  const pendingDisabled = status === 'pending';

  const items: MenuItemSpec[] = [
    {
      action: { kind: 'accept' },
      label: 'Accept',
      glyph: '✓',
      disabledReason: blocked
        ? 'Cannot accept — depends on a rejected entity.'
        : undefined,
    },
    { action: { kind: 'reject' }, label: 'Reject', glyph: '✗' },
    { action: { kind: 'pending' }, label: 'Mark pending', glyph: '◐' },
    { action: { kind: 'open-file' }, label: 'Open file', separator: true },
    ...(sysideAvailable
      ? [{ action: { kind: 'open-in-syside' as const }, label: 'Open in syside IDE' }]
      : []),
    { action: { kind: 'show-sysml-preview' }, label: 'Show SysML preview' },
    { action: { kind: 'show-svg-preview' }, label: 'Show SVG preview' },
    { action: { kind: 'why-proposed' }, label: 'Why is this proposed?' },
  ];

  const isDisabled = (item: MenuItemSpec): boolean => {
    if (item.action.kind === 'accept') return acceptDisabled;
    if (item.action.kind === 'reject') return rejectDisabled;
    if (item.action.kind === 'pending') return pendingDisabled;
    return false;
  };

  // Auto-focus first item on open
  useEffect(() => {
    if (open) {
      const first = itemRefs.current.find((el) => el !== null && !el.disabled);
      first?.focus();
    } else {
      itemRefs.current = [];
    }
  }, [open]);

  // Click-outside / Esc close
  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (ev: MouseEvent): void => {
      const target = ev.target as Node | null;
      if (menuRef.current && target && !menuRef.current.contains(target)) {
        onClose();
      }
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown, true);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const handleItemKey = (
    ev: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ): void => {
    if (ev.key === 'ArrowDown') {
      ev.preventDefault();
      // Find next non-disabled item, wrapping around
      for (let i = 1; i <= itemRefs.current.length; i++) {
        const next = itemRefs.current[(index + i) % itemRefs.current.length];
        if (next && !next.disabled) {
          next.focus();
          break;
        }
      }
    } else if (ev.key === 'ArrowUp') {
      ev.preventDefault();
      for (let i = 1; i <= itemRefs.current.length; i++) {
        const idx = (index - i + itemRefs.current.length) % itemRefs.current.length;
        const next = itemRefs.current[idx];
        if (next && !next.disabled) {
          next.focus();
          break;
        }
      }
    } else if (ev.key === 'Home') {
      ev.preventDefault();
      const first = itemRefs.current.find((el) => !el.disabled);
      first?.focus();
    } else if (ev.key === 'End') {
      ev.preventDefault();
      for (let i = itemRefs.current.length - 1; i >= 0; i--) {
        const cand = itemRefs.current[i];
        if (cand && !cand.disabled) {
          cand.focus();
          break;
        }
      }
    }
  };

  return (
    <div
      ref={menuRef}
      role="menu"
      aria-label={`Review options for ${entity.entityId}`}
      data-testid={`review-chip-context-menu-${entity.entityId}`}
      style={{
        position: 'fixed',
        top: anchor.y,
        left: anchor.x,
        background: 'var(--color-cream-50)',
        border: '1px solid var(--color-taupe-500)',
        borderRadius: '2px',
        boxShadow: 'var(--shadow-md)',
        padding: '0.25rem 0',
        minWidth: '11rem',
        zIndex: 1000,
        fontSize: '0.875rem',
        color: 'var(--color-fg)',
      }}
    >
      {items.map((item, index) => (
        <React.Fragment key={`${item.action.kind}-${item.label}`}>
          {item.separator && (
            <div
              role="separator"
              aria-hidden="true"
              style={{
                borderTop: '1px solid var(--color-taupe-300)',
                margin: '0.25rem 0',
              }}
            />
          )}
          <button
            ref={(el) => {
              if (el) itemRefs.current[index] = el;
            }}
            role="menuitem"
            type="button"
            disabled={isDisabled(item)}
            data-action={item.action.kind}
            title={item.disabledReason}
            onClick={() => {
              if (!isDisabled(item)) {
                onAction(item.action);
              }
            }}
            onKeyDown={(ev) => handleItemKey(ev, index)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '0.375rem 0.75rem',
              border: 'none',
              background: 'transparent',
              color: 'inherit',
              cursor: isDisabled(item) ? 'not-allowed' : 'pointer',
              opacity: isDisabled(item) ? 0.5 : 1,
              font: 'inherit',
            }}
          >
            {item.glyph && (
              <span aria-hidden="true" style={{ marginRight: '0.5rem' }}>
                {item.glyph}
              </span>
            )}
            {item.label}
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}
